/**
 * Decoys at L2 (IDEAS X3, the sector side). A squad leaves a decoy where it stands: an emitter that reads as that
 * squad, at its strength, for `rules.decoyDays`. Eyes from a distance — an antenna's or sensor's watch, a satellite
 * pass, a loud battle heard — cannot tell it from a squad (ruling 26: contacts are anonymous), so it enters the enemy's
 * knowledge as a squad standing there and the stand-in counts it. Looked at closely — a squad standing in the system,
 * a scout's look — it is exposed: that side drops it from its picture and the log says what it was. The stand-in
 * leaves one behind when its doctrine says sneak, so the system it slipped out of still reads as held by it.
 * Behind `rules.decoys`. Pure.
 */
import type { FactionId, KnownSquad, SectorState, Squad, SystemId } from './types';

export interface Decoy {
  id: number;
  faction: FactionId;
  at: SystemId;
  placedDay: number;
  /** The squad it reads as: name, strength and units as they were when it was placed. */
  looksLike: Pick<KnownSquad, 'name' | 'strength' | 'units' | 'doctrine'>;
  /** The factions that have looked closely and know it for what it is. */
  exposedTo: FactionId[];
}

/** The id a decoy carries in an enemy's knowledge (a squad id nobody else uses). */
export function decoyKey(d: Pick<Decoy, 'id'>): string {
  return `decoy-${d.id}`;
}

/** Is this report a decoy (a key only decoys use)? */
export function isDecoyKey(id: string): boolean {
  return id.startsWith('decoy-');
}

/** The decoys standing now (not past their days). */
export function liveDecoys(state: Pick<SectorState, 'decoys' | 'day' | 'rules'>): Decoy[] {
  return (state.decoys ?? []).filter(d => state.day < d.placedDay + state.rules.decoyDays - 1e-9);
}

/** A squad leaves a decoy where it stands (the rule on, the squad at a system, the supply in hand). */
export function placeDecoy(state: SectorState, squadId: string): SectorState {
  if (!state.rules.decoys) throw new Error('decoys are off in this sector');
  const q = state.squads.find(x => x.id === squadId);
  if (!q) throw new Error(`no squad ${squadId}`);
  if (q.at === null) throw new Error(`${q.name} cannot leave a decoy in transit`);
  const faction = state.factions.find(f => f.id === q.faction)!;
  if (faction.supply < state.rules.decoyCost) throw new Error(`${q.faction} cannot afford a decoy (${faction.supply} < ${state.rules.decoyCost})`);
  if (liveDecoys(state).some(d => d.faction === q.faction && d.at === q.at)) throw new Error(`a decoy of yours already stands at ${q.at}`);
  const id = (state.decoys ?? []).reduce((m, d) => Math.max(m, d.id), 0) + 1;
  const alive = q.units.filter(u => u.alive);
  const d: Decoy = { id, faction: q.faction, at: q.at, placedDay: state.day, looksLike: { name: q.name, strength: alive.length, units: alive.map(u => ({ ...u })), doctrine: { ...q.doctrine } }, exposedTo: [] };
  const sysName = state.systems.find(s => s.id === q.at)?.name ?? q.at;
  return {
    ...state,
    factions: state.factions.map(f => (f.id === q.faction ? { ...f, supply: f.supply - state.rules.decoyCost } : f)),
    decoys: [...(state.decoys ?? []), d],
    log: [...state.log, { day: state.day, kind: 'order', text: `${q.name} leaves a decoy at ${sysName} (until day ${(state.day + state.rules.decoyDays).toFixed(1)})`, squadId: q.id, systemId: q.at, faction: q.faction }],
  };
}

/** What a decoy reads as in an enemy's knowledge today. */
export function decoyReport(d: Decoy, day: number): KnownSquad {
  return { squadId: decoyKey(d), faction: d.faction, name: d.looksLike.name, at: d.at, transitTo: null, strength: d.looksLike.strength, units: d.looksLike.units.map(u => ({ ...u })), doctrine: { ...d.looksLike.doctrine }, seenDay: day };
}

/**
 * Close eyes on `systemId` expose `faction`'s view of every enemy decoy there: the decoy learns who knows it, the
 * report is dropped from that side's picture, and the log says what it was. No decoy there: the state unchanged.
 */
export function exposeDecoys(state: SectorState, faction: FactionId, systemId: SystemId): SectorState {
  const here = liveDecoys(state).filter(d => d.faction !== faction && d.at === systemId && !d.exposedTo.includes(faction));
  if (!here.length) return state;
  const k = state.knowledge[faction];
  const squads = k ? { ...k.squads } : undefined;
  if (squads) for (const d of here) delete squads[decoyKey(d)];
  const ids = new Set(here.map(d => d.id));
  const sysName = state.systems.find(s => s.id === systemId)?.name ?? systemId;
  return {
    ...state,
    decoys: (state.decoys ?? []).map(d => (ids.has(d.id) ? { ...d, exposedTo: [...d.exposedTo, faction] } : d)),
    ...(k && squads ? { knowledge: { ...state.knowledge, [faction]: { ...k, squads, ...(k.inbox ? { inbox: k.inbox.filter(r => !(r.squad && here.some(d => decoyKey(d) === r.squad!.squadId))) } : {}) } } } : {}),
    log: [...state.log, ...here.map(d => ({ day: state.day, kind: 'scout' as const, text: `${faction} finds ${d.looksLike.name} at ${sysName} was a decoy`, systemId, faction }))],
  };
}

/** Does `faction` have eyes in `systemId` itself (a living squad standing there)? */
export function closeEyes(state: Pick<SectorState, 'squads'>, faction: FactionId, systemId: SystemId): boolean {
  return state.squads.some(q => q.faction === faction && q.at === systemId && q.units.some(u => u.alive));
}

/**
 * The stand-in: a squad ordered to sneak out of a system leaves a decoy behind (the rule on, the supply in hand,
 * keeping a new squad's cost when reinforcements are on), so the system it left still reads as held by it.
 */
export function standInDecoys(state: SectorState, faction: FactionId, orders: Record<string, { kind: string; to?: string; stance?: string }>): SectorState {
  if (!state.rules.decoys) return state;
  let s = state;
  for (const [id, o] of Object.entries(orders)) {
    if (o.kind !== 'move' || o.stance !== 'sneak') continue;
    const q: Squad | undefined = s.squads.find(x => x.id === id);
    if (!q || q.faction !== faction || q.at === null || o.to === q.at) continue;
    const supply = s.factions.find(f => f.id === faction)?.supply ?? 0;
    const reserve = s.rules.reinforceDays > 0 ? s.rules.squadCost : 0;
    if (supply - s.rules.decoyCost < reserve) continue;
    try { s = placeDecoy(s, id); } catch { /* one already stands there */ }
  }
  return s;
}
